'use client'

import { IMediaItem } from '@/lib/types/media'
import { cn } from '@/lib/utils'
// HeroUI v3 removed Image component
import { useState } from 'react'
import { ImagePlaceholder } from '@/components/ui/image-placeholder'
import { Lightbox } from '../../ui/lightbox'

interface ProductMediaGalleryProps {
  media: IMediaItem[]
  productName?: string
  isFeatured?: boolean
  isPromoted?: boolean
  isSponsored?: boolean
}

export function ProductMediaGallery({ media, productName, isFeatured, isPromoted, isSponsored }: ProductMediaGalleryProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!media?.length) {
    return (
      <div className='relative mx-auto w-full'>
        <ImagePlaceholder
          alt={`Image needed: product photo of ${productName ?? 'this product'}`}
          className='aspect-4/3 w-full'
          iconSize='lg'
        />
      </div>
    )
  }

  return (
    <>
      {/* Main Image */}
      <div className='relative mx-auto w-full'>
        <div onClick={() => setIsLightboxOpen(true)} className='cursor-pointer relative'>
          <img
            src={media[currentIndex].url}
            alt={media[currentIndex].altText || ''}
            className={cn('object-cover bg-black rounded-lg', {
              'aspect-4/3 sm:aspect-3/4 md:aspect-4/3': !isFeatured && !isPromoted && !isSponsored,
              'aspect-3/4': isFeatured,
              'aspect-4/3': isPromoted,
              'aspect-3/4 border-4 border-secondary': isSponsored
            })}
          />
          {media.length > 1 && (
            <span className='absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full'>
              {currentIndex + 1}/{media.length}
            </span>
          )}
        </div>

        {/* Labels */}
        {isSponsored && (
          <div className='absolute top-4 right-4 z-10 px-2 py-1 text-sm text-white rounded-lg bg-secondary'>
            Sponsored
          </div>
        )}
        {isPromoted && (
          <div className='absolute top-4 right-4 z-10 px-2 py-1 text-sm text-white rounded-lg bg-primary'>Promoted</div>
        )}
        {isFeatured && (
          <div className='absolute top-4 right-4 z-10 px-2 py-1 text-sm text-white rounded-lg bg-primary'>Featured</div>
        )}
      </div>

      {/* Thumbnails */}
      {media.length > 1 && (
        <div className='flex overflow-x-auto gap-2 mt-4'>
          {media.map((item, index) => (
            <button
              key={`${item.url}-${index}`}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                'relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors',
                currentIndex === index ? 'border-primary' : 'border-transparent hover:border-primary/50'
              )}
            >
              <img
                src={item.url}
                alt={item.altText || `Product view ${index + 1}`}
                className='object-cover'
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Lightbox
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        media={media}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
      />
    </>
  )
}
